// src/components/Pages/Users.js

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme } from '@mui/material';

import TableSimple from '../Apm/Tables/TableSimple';
import { columnsUserManagement } from '../Apm/Tables/Columns/columnsUserManagement';
import {
  useDeleteUserMutation,
  useGetCompanyUsersQuery,
  useLogoutMutation,
  useRetrieveUserQuery,
} from '../../redux/features/authApi';

const MOBILE_COLUMN_IDS = new Set(['email', 'first_name', 'last_name']);

const Users = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: account } = useRetrieveUserQuery();

  const {
    data: users = [],
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
    refetch: refetchUsers,
  } = useGetCompanyUsersQuery();

  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();

  const [logout] = useLogoutMutation();

  const columns = useMemo(() => {
    const allColumns = columnsUserManagement();

    if (!smMediaDown) {
      return allColumns;
    }

    return allColumns.filter((column) => MOBILE_COLUMN_IDS.has(column.id));
  }, [smMediaDown]);

  const handleRefresh = useCallback(async () => {
    await refetchUsers().unwrap();
  }, [refetchUsers]);

  const handleDelete = useCallback(
    async (userId) => {
      await deleteUser(userId).unwrap();
    },
    [deleteUser]
  );

  const handleAfterDelete = useCallback(
    async (deletedUserId) => {
      const deletedOwnAccount = String(account?.id) === String(deletedUserId);

      if (!deletedOwnAccount) {
        return;
      }

      try {
        await logout().unwrap();
      } catch (error) {
        console.warn('Logout after account deletion failed:', error);
      } finally {
        navigate('/', {
          replace: true,
        });
      }
    },
    [account?.id, logout, navigate]
  );

  return (
    <TableSimple
      resource="users"
      columns={columns}
      popupTitle={t('popup.title.user_details')}
      data={users}
      addButtonText={t('button.add_user')}
      isFetching={isUsersLoading || isUsersFetching}
      isDeleting={isDeletingUser}
      onRefresh={handleRefresh}
      onDelete={handleDelete}
      onAfterDelete={handleAfterDelete}
    />
  );
};

export default Users;
